// Low-latency Web Audio engine: FX sampler pads + Echo / Reverb on the playing mix.
const FADE = 0.006;
let ctx = null;
let out = null;
let fxBus = null;
let mixNodes = null;
const buffers = new Map();
const voices = new Map();
const padVol = new Map();
const pressed = new Set();
const listeners = new Set();
const active = new Set();

const emit = () => listeners.forEach((fn) => fn(new Set(active)));
export const subscribe = (fn) => { listeners.add(fn); fn(new Set(active)); return () => listeners.delete(fn); };
const setActive = (key, on) => { if (on) active.add(key); else active.delete(key); emit(); };

export const getCtx = () => {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: "interactive" });
    out = ctx.createAnalyser();
    out.fftSize = 128;
    out.connect(ctx.destination);
    fxBus = ctx.createGain();
    fxBus.gain.value = 0.8;
    fxBus.connect(out);
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};
export const getAnalyser = () => { getCtx(); return out; };

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Beat-synced stand-in analyser for visuals when the mix is not routed through Web Audio (keeps lock-screen playback on iPhone).
export const fakeAnalyser = (getBpm, isPlaying) => ({
  frequencyBinCount: 64,
  getByteFrequencyData(arr) {
    const t = performance.now() / 1000;
    const phase = (t * getBpm() / 60) % 1;
    const kick = isPlaying() ? Math.pow(1 - phase, 3) : 0;
    for (let i = 0; i < arr.length; i++) {
      const wob = 0.5 + 0.5 * Math.sin(t * (2 + i * 0.37) + i);
      arr[i] = isPlaying() ? Math.min(255, (kick * (i < 8 ? 230 : 120) + wob * 90) * (1 - i / (arr.length * 1.4))) : 0;
    }
  },
});

export const loadBuffer = (id, url) => {
  if (!buffers.has(id)) {
    const p = fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
      .then((ab) => getCtx().decodeAudioData(ab));
    p.catch(() => buffers.delete(id));
    buffers.set(id, p);
  }
  return buffers.get(id);
};

export const setMaster = (v) => { getCtx(); fxBus.gain.setTargetAtTime(v, ctx.currentTime, 0.01); };
export const setPadVolume = (id, v) => {
  padVol.set(id, v);
  const voice = voices.get(id);
  if (voice) voice.gain.gain.setTargetAtTime(v, ctx.currentTime, 0.01);
};

const stopVoice = (id) => {
  const v = voices.get(id);
  if (!v) return;
  const t = ctx.currentTime;
  v.gain.gain.cancelScheduledValues(t);
  v.gain.gain.setValueAtTime(v.gain.gain.value, t);
  v.gain.gain.linearRampToValueAtTime(0, t + FADE);
  try { v.src.stop(t + FADE + 0.002); } catch (e) { /* already stopped */ }
  voices.delete(id);
  setActive(id, false);
};

// mode: shot | loop | hold | stutter ; slice = seconds (stutter) ; beat = seconds per beat
const startVoice = (id, buf, { mode, slice, beat }) => {
  const t = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(padVol.get(id) ?? 0.8, t + 0.002);
  src.connect(g).connect(fxBus);
  if (mode === "loop") {
    const beats = Math.floor(buf.duration / beat);
    src.loop = true;
    src.loopEnd = beats >= 1 ? beats * beat : buf.duration;
  }
  if (mode === "stutter") {
    src.loop = true;
    src.loopEnd = Math.min(slice, buf.duration);
  }
  src.onended = () => { if (voices.get(id)?.src === src) { voices.delete(id); setActive(id, false); } };
  src.start(t);
  voices.set(id, { src, gain: g });
  setActive(id, mode !== "shot");
};

export const padDown = async (id, url, opts) => {
  getCtx();
  pressed.add(id);
  const buf = await loadBuffer(id, url);
  const held = opts.mode === "hold" || opts.mode === "stutter";
  if (held && !pressed.has(id)) return;
  if (opts.mode === "loop" && voices.has(id)) { stopVoice(id); return; }
  stopVoice(id);
  startVoice(id, buf, opts);
};
export const padUp = (id, mode) => {
  pressed.delete(id);
  if (mode === "hold" || mode === "stutter") stopVoice(id);
};

// ---------- Mix chain: dry + echo send + reverb send ----------
const impulse = (seconds = 2.6, decay = 2.4) => {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
};

export const attachMedia = async (el) => {
  getCtx();
  if (mixNodes) return out;
  const src = ctx.createMediaElementSource(el);
  src.connect(out);
  const delay = ctx.createDelay(4);
  const fb = ctx.createGain(); fb.gain.value = 0.45;
  const echoSend = ctx.createGain(); echoSend.gain.value = 0;
  src.connect(echoSend).connect(delay).connect(fb).connect(delay);
  delay.connect(out);
  const verb = ctx.createConvolver(); verb.buffer = impulse();
  const verbSend = ctx.createGain(); verbSend.gain.value = 0;
  src.connect(verbSend).connect(verb).connect(out);
  mixNodes = { delay, echoSend, verbSend };
  return out;
};

export const setEcho = (on, beat) => {
  if (!mixNodes) return;
  const t = ctx.currentTime;
  mixNodes.delay.delayTime.setValueAtTime(Math.min(3.9, beat * 0.75), t);
  mixNodes.echoSend.gain.setTargetAtTime(on ? 0.6 : 0, t, 0.02);
  setActive("mix:echo", on);
};

export const setReverb = (on) => {
  if (!mixNodes) return;
  mixNodes.verbSend.gain.setTargetAtTime(on ? 0.7 : 0, ctx.currentTime, 0.03);
  setActive("mix:reverb", on);
};
